import React from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import i18n from 'i18next';
import DownloadButton from '../../utility/DownloadButton';
import DRTable from './DRTable';
import DRHeader from './DRHeader';
import Loading from '../../utility/Loading';
import { getDegreeClassesByYear } from '../GetDegreeClassesByYear';
import { getRequiredClassesByAcadPlan } from '../classes/drag-drop/dragDropUtil';
import { makePDF } from '../../../pdf';
import { getCoreSubjectStrings2 } from '../../../copy/ecap/classesSetupStrings';

class DegreeRequirements extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.pdf = React.createRef();
    this.state = {
      loading: true,
      degree: {},
      isMobile: null,
      yearSelected: 1,
    };
  }

  colorPrimary = {
    color: `${this.props.tenant.primary}`,
  };

  componentDidMount = async () => {
    this._isMounted = true;
    window.addEventListener('resize', this.onWindowResize);
    this.setState({
      isMobile: window.innerWidth < 800,
    });

    if (this.props.degree) {
      this.sendPDFdata();
    }
  };

  sendPDFdata = async () => {
    const requiredClassesByAcadPlan = getRequiredClassesByAcadPlan(
      this.props.degree.acadPlan,
      this.props.acadPlanClasses
    );
    const classesByYear = await getDegreeClassesByYear(
      requiredClassesByAcadPlan
    );

    if (classesByYear) {
      makePDF('degree-requirements', {
        degreeTitle: this.props.degree.titleEn,
        degree: this.props.degree,
        year1: classesByYear.year1,
        year2: classesByYear.year2,
        year3: classesByYear.year3,
        year4: classesByYear.year4,
        colors: {
          greyText: '#555555',
          backgroundGrey: '#F7F7F7',
          tableBorders: '#dbdbdb',
          secondary: this.props.tenant.secondary,
        },
        copy: {
          otherClass: i18n.t('Elective', { context: 'Academic Subjects' }),
          requirementsHeader: i18n.t('Degree Requirements Summary'),
          institutionName: 'Arizona State University',
          tableHeader1: i18n.t('Subject'),
          tableHeader2: i18n.t('Year {{number}}', { number: 1 }),
          tableHeader3: i18n.t('Year {{number}}', { number: 2 }),
          tableHeader4: i18n.t('Year {{number}}', { number: 3 }),
          tableHeader5: i18n.t('Year {{number}}', { number: 4 }),
          coreSubjectStrings: getCoreSubjectStrings2(),
        },
      })
        .then((data) => {
          this.setState({
            pdf: URL.createObjectURL(data),
            year1: classesByYear.year1,
            year2: classesByYear.year2,
            year3: classesByYear.year3,
            year4: classesByYear.year4,
          });
        })
        .catch((err) => {
          console.log('err: ', err);
          this.setState({ err });
        })
        .finally(() => {
          this.setState({ loading: false });
        });
    }
  };

  onWindowResize = debounce(() => {
    if (this._isMounted) this.setState({ isMobile: window.innerWidth < 800 });
  }, 100);

  componentDidUpdate = async (prevProps) => {
    if (this.props.degree !== undefined) {
      if (prevProps.degree.acadPlan !== this.props.degree.acadPlan) {
        this.sendPDFdata();
      }
    }
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  setYearSelected = (year) => {
    if (this._isMounted) {
      this.setState({
        yearSelected: year,
      });
    }
  };

  displayModalFooter() {
    const { isMobile, pdf, err } = this.state;

    return err ? (
      { err }
    ) : (
      <div className={isMobile ? 'modal-footer-mobile' : 'modal-footer'}>
        <a
          type="button"
          className="download-pdf-link"
          style={!pdf ? this.disabled : this.primaryBackground}
          href={pdf}
          download="Degree Requirements.pdf"
          rel="noopener noreferrer"
          target="_blank"
          title="Download My Degree Requirements PDF"
        >
          <button
            type="button"
            className="download-pdf"
            style={this.colorPrimary}
          >
            <span
              className={
                isMobile ? 'download-pdf-text-mobile' : 'download-pdf-text'
              }
            >
              Download requirements summary PDF
            </span>
            <DownloadButton color={this.props.tenant.primary} />
          </button>
        </a>
      </div>
    );
  }

  render() {
    const { year1, year2, year3, year4, loading, isMobile } = this.state;
    const { type } = this.props;
    const typeForClassname = 'modal';

    return (
      <div
        className={`degree-requirements-${typeForClassname}`}
        id={`degree-requirements-${type}`}
      >
        {loading ? (
          <Loading height="100%" />
        ) : (
          <div style={{ overflow: 'auto' }}>
            <DRHeader
              focus={this.props.focus}
              sendYearSelectedToParent={this.setYearSelected}
              yearSelected={this.state.yearSelected}
              tenant={this.props.tenant}
              closeDegreeRequirements={this.props.closeDegreeRequirements}
              type={type}
              isMobile={isMobile}
              showTitle={!type.includes('complete-plan')}
              title={this.props.degree.titleEn}
            />
            {type.includes('complete-plan') || type === 'pdf' ? (
              <div style={this.greyBackground} className="degree-title-header">
                {this.props.degree.titleEn}
              </div>
            ) : null}
            <div className={`degree-requirements-content-${typeForClassname}`}>
              <DRTable
                sendYearSelectedToParent={this.setYearSelected}
                yearSelected={this.state.yearSelected}
                degree={this.state.degree}
                tenant={this.props.tenant}
                type={type}
                isMobile={isMobile}
                year1={year1}
                year2={year2}
                year3={year3}
                year4={year4}
              />
            </div>
            {type === 'modal' ? this.displayModalFooter() : null}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const tenant = state.tenant;
  const auth = state.auth;
  const subjectDifficulty = state.ecap.subjectDifficulty;

  return { tenant, auth, subjectDifficulty };
};

export default connect(mapStateToProps, {})(DegreeRequirements);
